import useTheme from "../../../common/hooks/useTheme";
import { Theme } from "../../../providers/ThemeProvider";
import React, { useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyScreen from "../components/seviceBuilder/EmptyScreen";
import NewLogicModal from "../components/logicBuilder/NewLogicModal";
import PlusIcon from "../svg/PlusIcon";
import PrimaryButton from "../../../common/ui/PrimaryButton";
import SecondaryTabMenuPicker from "../components/common/SecondaryTabMenuPicker";
import PageLoader from "../../../common/ui/PageLoader";
import SavedLogicsScreen from "../components/logicBuilder/SavedLogicsScreen";
import { useSelector, useDispatch } from "react-redux";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../store/service-logic-builder-slice";
import { useNavigate } from "react-router-dom";


const LogicBuilder: React.FC = () => {
  const { loadData } = useOutletContext<{ loadData: () => Promise<any> }>();
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { savedLogics, isLoading, activeTab } = useSelector(
    (state: RootStateServiceLogicBuilder) => ({
      savedLogics: state.serviceLogicBuilder.savedLogics,
      isLoading: state.serviceLogicBuilder.loading,
      activeTab: state.serviceLogicBuilder.activeLogicTab,
    })
  );
  const [editingLogic, setEditingLogic] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Service-type and service filtering moved into the table's column headers,
  // matching the History and Lab Builder listings; this screen keeps only the
  // free-text search over the logic name.
  const [clearFiltersSlot, setClearFiltersSlot] = useState<HTMLElement | null>(
    null
  );

  const logicTabs = [
    { label: "PNOE logic", key: 0 },
    { label: "Custom", key: 1 },
  ];

  const filteredLogics = savedLogics
    // Tab 0 is the system logics, tab 1 the affiliate's own. Everything else
    // is filtered by the table, from the column headers.
    .filter((logic) =>
      activeTab === 0
        ? logic?.isSystem === 1
        : logic?.isSystem === 0 || !logic?.isSystem
    )
    .sort((a, b) => {
      const nameA = a.name?.toLowerCase() || "";
      const nameB = b.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });

  const hasLogics = !isLoading && savedLogics.length > 0;

  const handleLogicClick = (logic: any) => {
    navigate(`/client/services/logic/${logic.id}`);
  };

  const handleLogicSaved = async () => {
    // Refetch the data from ServiceScreen
    await loadData();
  };

  // Show loader while fetching data
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div style={styles.root}>
      {!hasLogics ? (
        <EmptyScreen type="logic" setOpenModal={setOpenModal} />
      ) : (
        <div style={styles.contentContainer}>
          {/* One toolbar line: tabs, then Clear filters, then New Logic.
              There is no search field — every column filters from its own
              header now, the Name column included, so a separate free-text
              box over the same data was a second way to do one thing. */}
          <div style={styles.tabsAndFiltersRow}>
            <SecondaryTabMenuPicker
              tabs={logicTabs}
              selectedTab={activeTab}
              setSelectedTab={(tab: number) =>
                dispatch(serviceLogicBuilderActions.setActiveLogicTab(tab))
              }
              isFullWidth={false}
            />
            <div style={styles.toolbarSpacer} />
            {/* The table portals its Clear-filters pill here, so applying a
                column filter does not push the rows down. */}
            <div ref={setClearFiltersSlot} style={styles.clearFiltersSlot} />
            <PrimaryButton
              label="New Logic"
              icon={<PlusIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />}
              onClick={() => {
                dispatch(serviceLogicBuilderActions.setActiveLogicTab(1)); // Switch to Custom tab
                setOpenModal(true);
              }}
              // Sharing the row with the tabs and the Clear-filters pill, the
              // button must not be squeezed into wrapping its label.
              sx={{ width: "160px", flexShrink: 0, whiteSpace: "nowrap" }}
            />
          </div>

          <SavedLogicsScreen
            filteredLogics={filteredLogics}
            onLogicClick={handleLogicClick}
            clearFiltersSlot={clearFiltersSlot}
          />
        </div>
      )}

      <NewLogicModal
        openModal={openModal}
        handleClose={() => {
          setOpenModal(false);
          setEditingLogic(null);
          setIsEditMode(false);
        }}
        onLogicSaved={handleLogicSaved}
        editingLogic={editingLogic}
        isEditMode={isEditMode}
      />
    </div>
  );
};

export default LogicBuilder;

const styles = {
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column" as "column",
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
    flexShrink: 0,
  },
  headerTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
  }),
  contentContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    flex: 1, // Take all remaining space
    minHeight: 0, // Important for flexbox child scrolling
  },
  tabsAndFiltersRow: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexShrink: 0,
  },
  toolbarSpacer: {
    flex: 1,
  },
  clearFiltersSlot: {
    display: "flex",
    alignItems: "center",
  },
};
